import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Box from "./box";
import { IconBuilder } from "../../icons/IconBuilder";
import HeroButton from "../../HeroUi/Button";
import { useNavigate } from "react-router-dom";
import { useCallback, useState } from "react";
import { testConnection } from "/app/src/helpers/connectionsStatus";
import { Connection } from "/app/src/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import { connectionService } from "/app/src/services";
import useSetting from "../../settings/setting";
import { useAuthState } from "/app/src/contexts/authentication";
import { convertStringToBoolean } from "/app/src/helpers";

/**
 * Checks if all elements in the connections array are non-zero.
 *
 * @param {number[]} connections - Array of connection statuses.
 * @returns {boolean} - Returns true if all connections are non-zero, otherwise false.
 */
function isConnected(connections: Connection[]) {
  if (!connections) return false;
  if (connections.length === 0) return false;

  // Ensure we don't access `status` on an undefined/null element.
  return connections.every((conn) => {
    if (!conn) return false;
    return conn.status !== 0;
  });
}

/**
 * A component that conditionally renders its children or a disabled view.
 *
 * @param {Object} props - The component props.
 * @param {string} props.name - The name of the item.
 * @param {string} props.message - The message to display when the component is disabled.
 * @param {number[]} [props.connections] - An array of connection identifiers.
 * @param {JSX.Element} [props.children] - The child JSX element to render when enabled.
 * @returns {JSX.Element} The rendered component.
 */
export default function DisabledCheck({
  name,
  connections,
  children = null,
  loading,
}: {
  name: string;
  connections?: Connection[] | undefined;
  children?: JSX.Element;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isTesting, setIsTesting] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuthState();
  const { settingValue: navigationMenus } = useSetting({
    name: "navigationMenus",
    userId: user?.id,
  });

  /**
   * Determines the navigation menu height based on the navigationMenus setting.
   * @returns {number} The navigation menu height in pixels.
   */
  const getNavigationMenuValue = () => {
    if (convertStringToBoolean(navigationMenus)) {
      return 116;
    } else {
      return 214;
    }
  };

  const navigateToConnections = useCallback(() => {
    navigate("/settings/kardex");
  }, [navigate]);

  const { mutateAsync: updateConnection } = useMutation({
    mutationFn: (connection: Connection) => {
      return connectionService
        .updateSingle(connection.id, connection)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["powerpickConnection"], (old: Connection[]) => {
        return old.map((conn) => (conn.id === response.id ? response : conn));
      });
    },
  });

  const testHandler = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();

      if (!connections) return;

      connections.filter(Boolean).forEach((connection: Connection) => {
        if (!connection?.status) {
          testConnection(setIsTesting, connection, updateConnection).finally(
            () => {
              queryClient.invalidateQueries(["powerpickConnection"]);
            },
          );
        }
      });
    },
    [connections, updateConnection, queryClient],
  );

  // Use an explicit `loading` prop when provided. Otherwise, fall back to
  // treating `connections === undefined` as loading (query not resolved).
  // Returning `null` prevents navigation to the disabled page while loading.
  const isLoading = loading ?? connections === undefined;
  if (isLoading) return null;

  if (isConnected(connections)) {
    return children;
  } else {
    return (
      <>
        <Helmet>
          <title>
            {name} {t("translation:disabled")} - ItemPath
          </title>
        </Helmet>

        <div
          className={`flex items-center justify-center min-h-[400px] h-[calc(100vh-${getNavigationMenuValue()}px)]`}
        >
          <Box className="w-[550px] flex flex-col items-center justify-center">
            <IconBuilder
              className="my-4"
              icon="Warning"
              size={75}
              color="#FF5E2F"
            />
            <h1 className="font-bold text-[18px]">
              {t("translation:power_pick_connection_failed")}
            </h1>
            <p className="text-center">
              {name} {t("translation:unable_to_connect_power_pick")}
            </p>
            <div className="flex space-x-2">
              <HeroButton
                className="mt-4"
                radius="sm"
                color="primary"
                onClick={navigateToConnections}
              >
                {t("translation:go_to_connections")}
              </HeroButton>
              <HeroButton
                className="mt-4"
                radius="sm"
                color="primary"
                variant="bordered"
                isDisabled={isTesting}
                onClick={testHandler}
              >
                {t("translation:test_connections")}
              </HeroButton>
            </div>
          </Box>
        </div>
      </>
    );
  }
}
